<template>
  <header class="front-header">
    <button v-on:click="skipToContent" class="skip-to-content">Skip to content</button>
    <div class="front-header-cookies" v-if="isShownCookiesNot">
      <div class="front-cookies-container">
        <div class="front-header-cookies-content">
          This website uses cookies, so we can improve your experience here.
          By continuing to browse the site, you are agreeing to our use of cookies.
          Read more about this in our
          <router-link :to="{name: 'cookies'}">Cookies Policy</router-link>
        </div>
        <div class="front-header-cookies-control">
          <button type="button" @click="setCookiesPermission">I agree</button>
        </div>
      </div>
    </div>
    <!-- .front-header-top -->
    <div class="front-header-top">
      <!-- .myContainer -->
      <div class="myContainer front-header-top__inner">
        <router-link to="/" title="The Clear Company logo"
                     class="front-header-logo"
                     v-bind:style="{backgroundImage: this.customLogo ? 'url(' + getFullServerPath(this.customLogo) + ')' : '' }"
                     :class="this.customLogo ? 'industry-logo' : ''">
        </router-link>
        <ul class="front-header-menu" v-if="!getDeviceType('mobile')">
          <li v-for="item in headerData">
            <a :href="item.url" :title="item.alt">{{ item.title }}</a>
          </li>
          <li class="login-trigger">
            <button aria-label="Login"
                    aria-haspopup="dialog"
                    :aria-expanded="this.loginOpen"
                    :class="[{ 'active': this.loginOpen }]"
                    @click.prevent.stop="openSubmenu('login')">Login</button>
            <div class="front-header-login"
                 aria-label="Login"
                 role="dialog"
                 :class="[{ 'has-error': isError || errors.has('Email') || errors.has('Password') }]"
                 ref="loginContainer"
                 v-show="this.loginOpen && !getDeviceType('mobile')">
              <form class="front-header-login-inner"
                    aria-label="Login"
                    v-on:submit.prevent="authenticate()">
                <div class="form-group"
                     :class="[{ 'active': loginData.email !== '' || emailFocus, 'has-error': isError || errors.has('Email') }]">
                  <label for="header-email">Email</label>
                  <input class="header-login-input email-type"
                         :class="[{ 'border-1-50': loginData.email !== '' || emailFocus, 'has-error': isError || errors.has('Email') }]"
                         id="header-email"
                         @keyup.enter="authenticate()"
                         @focus="emailFocus = true"
                         @blur="emailFocus = false"
                         v-model="loginData.email"
                         v-validate="'required|email'"
                         data-vv-name="Email"
                         title="Login email"
                         type="email" />
                </div>
                <div class="form-group"
                     :class="[{ 'active': loginData.password !== '' || passwordFocus, 'has-error': isError || errors.has('Password') }]">
                  <label for="header-password">Password</label>
                  <span class="front-header-login-password-show"
                        @click.prevent="showPassword()"></span>
                  <input class="header-login-input password-type"
                         :class="[{ 'border-1-50': loginData.password !== '' || passwordFocus, 'has-error': isError || errors.has('Password') }]"
                         id="header-password"
                         @keyup.enter="authenticate()"
                         @focus="passwordFocus = true"
                         @blur="passwordFocus = false"
                         v-model="loginData.password"
                         v-validate="'required'"
                         data-vv-name="Password"
                         title="Login password"
                         :type="passwordType" />
                </div>
                <div class="login-form-error"
                     aria-live="assertive">
                  <span v-if="this.errors.has('Email')">{{ this.errors.first('Email') }}</span>
                  <span v-if="this.errors.has('Password')">{{ this.errors.first('Password') }}</span>
                  <span v-if="loginInProgress" class="visually-hidden">Submitting</span>
                  <span v-if="loginErrorMessage">{{ loginErrorMessage }}</span>
                </div>
                <div class="front-header-login-actions">
                  <button class="login background-1-100"
                          :class="[{ 'active-logging': loginInProgress }]"
                  >Login</button>
                  <router-link :to="{ name: 'forgot-password' }"
                               title="Forgot password"
                               class="reset-password">Forgot password?</router-link>
                </div>
              </form>
            </div>
          </li>
          <li class="accessibility-trigger">
            <a href="#"
               title="Open accessibility"
               @click.prevent.stop="openSubmenu('accessibility')"></a>
            <div class="front-header-capabilities" ref="accessibilityContainer"
                 v-if="this.accessibilityOpen && !getDeviceType('mobile')">
              <div class="reciteMe-container">
                <button class="reciteme background-1-100" id="reciteMe">Speak or translate</button>
              </div>
              <div class="front-header-capabilities-inner">
                <ul class="text-resize-controls">
                  <li><button class="text-resize-controls__button text-resize-controls__normal" id="accessibilityFontSize_0" title="Normal text size" @click.prevent="resizeText(0)">A</button></li>
                  <li><button class="text-resize-controls__button text-resize-controls__medium" id="accessibilityFontSize_1" title="Medium text size" @click.prevent="resizeText(1)">A</button></li>
                  <li><button class="text-resize-controls__button text-resize-controls__large" id="accessibilityFontSize_2" title="Big text size" @click.prevent="resizeText(2)">A</button></li>
                </ul>
                <div class="color-change-controls">
                  <button class="color-change-controls__button color-change-controls__normal" id="accessibilityColorPalette_0" title="Normal view" @click.prevent="changeVisibilityThem(0)"></button>
                  <button class="color-change-controls__button color-change-controls__contrast" id="accessibilityColorPalette_1" title="High contrast view" @click.prevent="changeVisibilityThem(1)"></button>
                  <button class="color-change-controls__button color-change-controls__dyslexia" id="accessibilityColorPalette_2" title="Dyslexia friendly view" @click.prevent="changeVisibilityThem(2)"></button>
                </div>
              </div>
            </div>
          </li>
        </ul>
        <a href="#" class="front-header-mobile-menu-trigger"
             @click.prevent.stop="mobileMenuShown = !mobileMenuShown"
             v-if="getDeviceType('mobile')">
          <span></span>
          <span></span>
          <span></span>
        </a>
        <div class="front-header-mobile-menu"
             ref="mobileMenuContainer"
             :class="[{ 'login-opened': this.mobileLoginOpen }]"
             v-if="mobileMenuShown && getDeviceType('mobile')">
          <div class="front-header-mobile-menu-inner">
            <div class="color-change-controls">
              <button class="color-change-controls__button color-change-controls__normal" id="accessibilityColorPalette_0" title="Normal view" @click.prevent="changeVisibilityThem(0)"></button>
              <button class="color-change-controls__button color-change-controls__contrast" id="accessibilityColorPalette_1" title="High contrast view" @click.prevent="changeVisibilityThem(1)"></button>
              <button class="color-change-controls__button color-change-controls__dyslexia" id="accessibilityColorPalette_2" title="Dyslexia friendly view" @click.prevent="changeVisibilityThem(2)"></button>
            </div>
            <ul class="front-header-mobile-menu-items">
              <li v-for="item in headerData">
                <a :href="item.url" :title="item.alt">{{ item.title }}</a>
              </li>
              <li class="login-trigger">
                <a href="#"
                   :class="[{ 'active': this.mobileLoginOpen }]"
                   @click.prevent="mobileLoginOpen = !mobileLoginOpen">Login</a>
              </li>
            </ul>
            <div class="front-header-login" ref="loginContainer"
                 v-if="mobileLoginOpen && getDeviceType('mobile')">
              <div class="front-header-login-inner">
                <div class="form-group"
                     :class="[{ 'active': loginData.email !== '' || emailFocus, 'has-error': isError }]">
                  <label for="header-email">Email</label>
                  <input class="header-login-input email-type"
                         :class="[{ 'border-1-50': loginData.email !== '' || emailFocus, 'has-error': isError }]"
                         id="header-email"
                         @keyup.enter="authenticate()"
                         @focus="emailFocus = true"
                         @blur="emailFocus = false"
                         v-model="loginData.email"
                         type="email" />
                </div>
                <div class="form-group"
                     :class="[{ 'active': loginData.password !== '' || passwordFocus, 'has-error': isError }]">
                  <label for="header-password">Password</label>
                  <span class="front-header-login-password-show"
                     @click.prevent="showPassword()"></span>
                  <input class="header-login-input password-type"
                         :class="[{ 'border-1-50': loginData.password !== '' || passwordFocus, 'has-error': isError }]"
                         id="header-password"
                         @keyup.enter="authenticate()"
                         @focus="passwordFocus = true"
                         @blur="passwordFocus = false"
                         v-model="loginData.password"
                         :type="passwordType" />
                </div>
                <div class="login-form-error" v-if="isError">
                  {{ loginErrorMessage }}
                </div>
                <div class="front-header-login-actions">
                  <router-link :to="{ name: 'forgot-password' }" class="reset-password">Forgot password?</router-link>
                  <a href="#" class="login background-1-100"
                     :class="[{ 'active-logging': loginInProgress }]"
                     @click.prevent="authenticate()">Login</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- END:.myContainer -->
    </div>
    <!-- END:.front-header-top -->
  </header>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'
  import api from '@/api'

  export default {
    name: 'authorization-header',
    data () {
      return {
        isShownCookiesNot: false,
        accessibilityOpen: false,
        loginOpen: false,
        mobileLoginOpen: false,
        isError: false,
        loginErrorMessage: '',
        loginData: {
          email: '',
          password: '',
          grantType: 'password',
          clientId: '2_659vq9a3czggcws88wcws8s4kc8gc8gcss4wog4cwcsk08ggkg',
          clientSecret: '5ncmjzrt9ps8808cskk8ogwwgwkgks8ggc0kskg0cw48s08ks4'
        },
        emailFocus: false,
        passwordFocus: false,
        loginInProgress: false,
        passwordType: 'password',
        mobileMenuShown: false,
        windowWidth: window.innerWidth,
        headerData: [
          {
            title: '',
            url: '',
            alt: ''
          }
        ],
        customLogo: false
      }
    },
    computed: {
      ...mapGetters([])
    },
    methods: {
      ...mapActions([
        'redirect',
        'resizeText',
        'changeVisibilityThem',
        'setFontSizeActive',
        'setColorPaletteActive',
        'authLogout'
      ]),
      getFullServerPath (fileName) {
        return api.serverContenImages + fileName
      },
      getHeaderData () {
        let self = this
        let body = {
          slug: 'landing-header'
        }
        Promise.all([
          this.$http.post(api.URLS.staticpage + '/landing-header', body, api.headersAuthSettings)
        ]).then(function (data) {
          let pageData = JSON.parse(data[0].data.body)
          self.headerData = pageData
          self.pageEditMode = false
        })
      },
      getIndustryLogo () {
        let industryId = localStorage.getItem('userIndustry')
        if (!industryId) {
          return
        }
        let data = {
          id: industryId
        }
        this.$http.post(api.URLS.industry + '/logo', data, api.headersAuthSettings).then((res) => {
          this.customLogo = res.body.logo
        }).catch((err) => {
          console.log(err)
        })
      },
      openSubmenu (menu) {
        if (menu === 'accessibility') {
          this.accessibilityOpen = !this.accessibilityOpen
          this.loginOpen = false
          setTimeout(function () {
            let button = document.getElementById('reciteMe')
            if (button) {
              button.focus()
            }
          }, 100)
        }
        if (menu === 'login') {
          this.loginOpen = !this.loginOpen
          this.accessibilityOpen = false
          setTimeout(function () {
            let email = document.getElementById('header-email')
            if (email) {
              email.focus()
            }
          }, 100)
        }
      },
      authenticate () {
        if (this.disableAPI) return
        this.loginInProgress = true
        this.$validator.validateAll().then((valid) => {
          if (!valid) {
            this.loginInProgress = false
            return
          }
          this.$store.commit('authLogin')
          let auth = {
            grant_type: this.loginData.grantType,
            client_id: this.loginData.clientId,
            client_secret: this.loginData.clientSecret,
            username: this.loginData.email,
            password: this.loginData.password
          }
          this.disableAPI = true
          this.$http.post(api.URLS.login, auth).then((res) => {
            this.isError = false
            document.cookie = 'Recite.Persist=; Max-Age=-99999999;'
            let token = res.body.access_token
            let headers = {headers: {'Authorization': `Bearer ${token}`}}
            this.$http.put(api.URLS.profile, {}, headers)
              .then((res) => {
                this.disableAPI = false
                this.loginInProgress = false
                localStorage.setItem('token', token)
                this.$store.commit('authLoginSuccess')
                let userData = {
                  'firstName': res.body.first_name ? res.body.first_name : '',
                  'lastName': res.body.last_name ? res.body.last_name : '',
                  'userRole': res.body.role.slug,
                  'company': res.body.company ? res.body.company.id : null
                }
                if (['ca', 'gm'].indexOf(res.body.role.slug) > -1 && res.body.consent_status === 0) {
                  localStorage.setItem('consentHead', 'true')
                }
                localStorage.setItem('consentSeen', res.body.consent_seen)
                localStorage.setItem('userData', JSON.stringify(userData))
                localStorage.setItem('cookieFixed', true)
                localStorage.setItem('userIndustry', (res.body.company && res.body.company.industry) ? res.body.company.industry.id : 1)
                document.getElementById('app').setAttribute('data-industry', (res.body.company && res.body.company.industry) ? res.body.company.industry.id : 1)
                if (res.body.company) {
                  localStorage.setItem('userCompanyName', res.body.company.name)
                }
                if (res.body.company && res.body.company.industry) {
                  localStorage.setItem('userIndustryName', res.body.company.industry.name)
                }
                this.$store.commit('authLoginSetUsername', userData.firstName)
                this.redirect()
              })
              .catch((err) => {
                this.disableAPI = false
                this.loginInProgress = false
                if (err.status === 401) {
                  localStorage.removeItem('token')
                  localStorage.removeItem('userData')
                  localStorage.removeItem('consentSeen')
                  localStorage.removeItem('consentHead')
                  localStorage.removeItem('userIndustryName')
                  localStorage.removeItem('userCompanyName')
                  this.isError = true
                  this.loginErrorMessage = 'Your account is inactive. Please contact support.'
                }
              })
          }).catch(() => {
            this.disableAPI = false
            this.isError = true
            this.loginErrorMessage = 'Invalid credentials'
            this.loginInProgress = false
          })
        })
      },
      setCookiesPermission () {
        this.setCookie('useCookies', true, {expires: new Date(2030, 0, 0)})
        this.isShownCookiesNot = false
      },
      setCookie (name, value, options) {
        options = options || {}

        var expires = options.expires

        if (typeof expires === 'number' && expires) {
          var d = new Date()
          d.setTime(d.getTime() + expires * 1000)
          expires = options.expires = d
        }
        if (expires && expires.toUTCString) {
          options.expires = expires.toUTCString()
        }

        value = encodeURIComponent(value)

        var updatedCookie = name + '=' + value

        for (var propName in options) {
          updatedCookie += ';' + propName
          var propValue = options[propName]
          if (propValue !== true) {
            updatedCookie += '=' + propValue
          }
        }

        document.cookie = updatedCookie
      },
      getCookie (name) {
        let matches = document.cookie.match(new RegExp(
            '(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'
        ))
        return matches ? decodeURIComponent(matches[1]) : undefined
      },
      hideSubMenus (e) {
        let loginContainer = this.$refs.loginContainer
        let accessibilityContainer = this.$refs.accessibilityContainer
        let mobileMenuContainer = this.$refs.mobileMenuContainer
        let target = e.target
        if (loginContainer && loginContainer !== target && !loginContainer.contains(target)) {
          this.loginOpen = false
        }
        if (accessibilityContainer && accessibilityContainer !== target && !accessibilityContainer.contains(target)) {
          this.accessibilityOpen = false
        }
        if (mobileMenuContainer && mobileMenuContainer !== target && !mobileMenuContainer.contains(target)) {
          this.mobileLoginOpen = false
        }
      },
      showPassword () {
        this.passwordType = this.passwordType === 'password' ? 'text' : 'password'
      },
      handleWindowResize (event) {
        this.windowWidth = event.currentTarget.innerWidth
      },
      getDeviceType (check) {
        let width = this.windowWidth
        let deviceName = ''
        if (width > 940) {
          deviceName = 'desktop'
        } else if (width > 640) {
          deviceName = 'tablet'
        } else {
          deviceName = 'mobile'
        }
        return check === deviceName
      },
      skipToContent: function (event) {
        event.preventDefault()
        const elm = document.getElementsByTagName('h1')[0]
        if (!elm) {
          return
        }
        elm.setAttribute('tabindex', '-1')
        elm.focus()
        this.scrollTo(document.body, elm, elm.offsetTop - 20, 1500)
      },
      scrollTo (element, target, to, duration) {
        if (duration <= 0) {
          return
        }
        const difference = to - element.scrollTop
        const perTick = difference / duration * 10

        setTimeout(function () {
          element.scrollTop = element.scrollTop + perTick
          if (element.scrollTop === to) {
            return
          }
          this.scrollTo(element, to, duration - 10)
        }, 20)
        target.focus({preventScroll: true})
      }
    },
    created () {
      this.isShownCookiesNot = !this.getCookie('useCookies')
    },
    beforeMount () {
      this.getHeaderData()
      this.getIndustryLogo()
    },
    mounted () {
      window.addEventListener('resize', this.handleWindowResize)
      document.addEventListener('click', this.hideSubMenus)
    },
    beforeDestroy () {
      window.removeEventListener('resize', this.handleWindowResize)
      document.removeEventListener('click', this.hideSubMenus)
    },
    watch: {
      'accessibilityOpen' (value) {
        if (value && value === true) {
          let self = this
          setTimeout(function () {
            self.setFontSizeActive()
            self.setColorPaletteActive()
          }, 0)
        }
      },
      'mobileMenuShown' (value) {
        if (value && value === true) {
          let self = this
          setTimeout(function () {
            self.setColorPaletteActive()
          }, 0)
        }
      }
    }
  }
</script>

<style src="@/assets/scss/main/common/authorization-header.scss" lang="scss" scoped />



// WEBPACK FOOTER //
// AuthorizationHeader.vue?0c00b95d